/**
 * Lógica pura do "fechar ao clicar fora / Esc" das camadas sobrepostas (popover, dropdown, menu) — usada por
 * `useDismiss`. Sem DOM real: só precisa de `contains`/`closest`, então roda no `node:test`.
 *
 * Modelo: uma pilha de camadas abertas (a última aberta no topo). Cada camada tem regiões "de dentro" (gatilho +
 * painel, inclusive painel em portal). Um toque fora fecha as camadas do topo para baixo até achar a camada que
 * contém o alvo — clicar num dropdown filho não fecha o pai; clicar no pai fora do filho fecha só o filho.
 */

/** O mínimo de um nó do DOM que a lógica usa. */
export interface NodeLike {
    contains(other: NodeLike | null): boolean;
}

export interface DismissLayer {
    /** Regiões que contam como "dentro" (gatilho, painel, wrapper). `null` = ainda não montado. */
    regions: () => readonly (NodeLike | null | undefined)[];
    /** Fecha ao tocar fora (padrão true). */
    outside: boolean;
    /** Fecha com Esc (padrão true). */
    escape: boolean;
}

/** O alvo está dentro de alguma das regiões? */
export function isInside(regions: readonly (NodeLike | null | undefined)[], target: NodeLike | null): boolean {
    if (!target) return false;
    return regions.some((r) => !!r && (r === target || r.contains(target)));
}

/**
 * Camadas a fechar num toque em `target`, do topo para baixo; para na primeira que contém o alvo.
 * Uma camada com `outside: false` (ex.: só Esc) não fecha nem bloqueia as de baixo.
 * `inside` = o alvo caiu dentro de alguma camada que continua aberta.
 */
export function layersToClose<L extends DismissLayer>(stack: readonly L[], target: NodeLike | null): { close: L[]; inside: boolean } {
    const close: L[] = [];
    for (let i = stack.length - 1; i >= 0; i--) {
        const l = stack[i];
        if (isInside(l.regions(), target)) return { close, inside: true };
        if (l.outside) close.push(l);
    }
    return { close, inside: false };
}

/** Camada que o Esc fecha: a do topo que aceita Esc (as de baixo esperam o próximo Esc). */
export function escapeTarget<L extends DismissLayer>(stack: readonly L[]): L | null {
    for (let i = stack.length - 1; i >= 0; i--) if (stack[i].escape) return stack[i];
    return null;
}

/** O mínimo de um elemento para saber se é interativo. */
export interface ElementLike extends NodeLike {
    closest(selector: string): unknown;
}

/** Controles nativos que têm ação própria no clique (e não disparam a ativação de um `<label>` ancestral). */
export const NATIVE_INTERACTIVE = 'button, a[href], input, select, textarea, summary, [contenteditable="true"]';
/** Interativos em geral (nativos + papéis ARIA clicáveis). */
export const INTERACTIVE = `${NATIVE_INTERACTIVE}, [role="button"], [role="option"], [role="menuitem"], [role="tab"], [role="checkbox"], [role="switch"], [role="link"]`;

const isElementLike = (t: unknown): t is ElementLike => !!t && typeof (t as ElementLike).closest === 'function';

/** O alvo (ou um ancestral) é interativo? Nós de texto/sem `closest` contam como não interativos. */
export function isInteractive(target: unknown, selector = INTERACTIVE): boolean {
    return isElementLike(target) && target.closest(selector) != null;
}

/**
 * O clique que segue um toque fora que fechou alguma camada deve ser engolido? Sim quando caiu fora de tudo e em
 * algo não interativo (fundo de modal, texto de `<label>`, linha de tabela) — como fazia o antigo fundo invisível,
 * evita fechar o modal por baixo ou reabrir o popover pela ativação do `<label>`. Clicar em outro botão/link passa.
 */
export function shouldSwallowClick(closedSomething: boolean, insideRemaining: boolean, target: unknown): boolean {
    return closedSomething && !insideRemaining && !isInteractive(target);
}

/**
 * Clique dentro de uma camada (gatilho/painel) num ponto sem controle nativo: impedir a ação padrão evita que um
 * `<label>` ancestral "clique" no primeiro controle dele (o gatilho), o que fechava/reabria o popover. Só vale para um
 * `<label>` que envolve a camada inteira — um `<label>` de checkbox dentro do painel continua funcionando.
 */
export function shouldBlockLabelActivation(target: unknown, regions: readonly (NodeLike | null | undefined)[]): boolean {
    if (!isElementLike(target) || target.closest(NATIVE_INTERACTIVE) != null) return false;
    const label = target.closest('label') as NodeLike | null;
    return !!label && regions.some((r) => !!r && label.contains(r));
}
